// src/services/ToastService.ts
import { toast, ToastOptions, Id, TypeOptions } from 'react-toastify';

// Match the type with react-toastify's TypeOptions
type ToastType = TypeOptions; // This includes 'success' | 'error' | 'warning' | 'info' | 'default' | 'dark'

class ToastService {
  private static instance: ToastService;

  private constructor() { }

  static getInstance(): ToastService {
    if (!ToastService.instance) {
      ToastService.instance = new ToastService();
    }
    return ToastService.instance;
  }

  /**
   * Default toast options for top-right position
   */
  private getDefaultOptions(type: ToastType = 'info'): Partial<ToastOptions> {
    const baseOptions: Partial<ToastOptions> = {
      position: "top-right",
      autoClose: 4000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
      theme: "colored",
    };

    if (type === 'error') {
      return {
        ...baseOptions,
        autoClose: 4000,
      };
    }

    return baseOptions;
  }

  /**
   * Get CSS class based on toast type
   */
  private getToastClassName(type: ToastType): string {
    switch (type) {
      case 'success': return 'bg-success text-white';
      case 'error': return 'bg-danger text-white';
      case 'warning': return 'bg-warning text-white';
      case 'info': return 'bg-info text-white';
      case 'default': return 'bg-primary text-white';
      default: return 'bg-primary text-white';
    }
  }

  /**
   * Show a toast notification
   */
  show(message: string | React.ReactNode, type: ToastType = 'info', options?: Partial<ToastOptions>): Id {
    const defaultOptions = this.getDefaultOptions(type);
    const className = this.getToastClassName(type);

    const finalOptions: ToastOptions = {
      ...defaultOptions,
      ...options,
      className: options?.className ? `${className} ${options.className}` : className,
    };

    return toast(message, { ...finalOptions, type });
  }

  /**
   * Show success toast
   */
  success(message: string | React.ReactNode, options?: Partial<ToastOptions>): Id {
    return this.show(message, 'success', options);
  }

  /**
   * Show error toast
   */
  error(message: string | React.ReactNode, options?: Partial<ToastOptions>): Id {
    return this.show(message, 'error', options);
  }

  /**
   * Show warning toast
   */
  warning(message: string | React.ReactNode, options?: Partial<ToastOptions>): Id {
    return this.show(message, 'warning', options);
  }

  /**
   * Show info toast
   */
  info(message: string | React.ReactNode, options?: Partial<ToastOptions>): Id {
    return this.show(message, 'info', options);
  }

  /**
   * Show loading toast (doesn't auto-close)
   */
  loading(message: string | React.ReactNode = 'Loading...', options?: Partial<ToastOptions>): Id {
    return this.show(message, 'info', {
      autoClose: false,
      closeButton: false,
      hideProgressBar: true,
      ...options,
    });
  }

  /**
   * Update an existing toast - FIXED TYPE
   */
  update(id: Id, options: Partial<ToastOptions & { render?: string | React.ReactNode }>) {
    toast.update(id, options);
  }

  /**
   * Dismiss a toast
   */
  dismiss(id?: Id) {
    if (id) {
      toast.dismiss(id);
    } else {
      toast.dismiss();
    }
  }

  /**
   * Clear all toasts
   */
  clear() {
    toast.clearWaitingQueue();
    toast.dismiss();
  }
}

export default ToastService.getInstance();